import React, {useEffect, useMemo, useState} from 'react';
import Header from '../../../components/ListViewHeader/Header';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {actions} from '../../../store';
import {ReduxState} from '../../../store/types';
import DataGrid from '../../../components/DataGrid/DataGrid';
import {PageChangeEvent, PaginationMode} from '../../../components/DataGrid/types';
import {AutoAttendantListItem} from '../../../store/types/AutoAttendant';
import CallHistoryDialog from '../../../components/Extensions/CallHistoryDialog';
import Dialog from '../../../components/AutoAttendants/CreateNewAutoAttendant/Dialog';
import {generateColumns, generateSvColumns, useStyles} from './utils';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../../components/AlertDialog/DialogContainer';
import usePageTitle from '../../../hooks/usePageTitle';
import PermissionProvider from '../../../components/PermissionProvider/PermissionProvider';
import {Permission} from '../../../store/types/Permission';
import PermissionPlaceholder from "../../../components/PermissionProvider/PermissionPlaceholder";
import {useIndividualTypeHook} from "../../../hooks/agents/useIndividualTypeHook";
import {useAutoAttendantsListLogic} from "../../../hooks/autoAttendants/useAutoAttendantsListLogic";
import FiltersComponent from "./FiltersComponent";
import {AAListFilters} from "./FiltersComponent.utils";

const AutoAttendantsList = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    usePageTitle();

    const {isSupervisor} = useIndividualTypeHook()
    const [
        createNewAutoAttendantModalVisible,
        setCreateNewAutoAttendantModalVisible,
    ] = useState(false);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [forceSearch, setForceSearch] = useState(false);
    const [forceFirstPage, setForceFirstPage] = useState(false);

        const {
            callHistoryModalConfig,
            showCallHistory,
            resetCallHistoryDialog,
            removeModal,
            showRemoveExtensionModal,
            closeRemoveDialog,
            deleteExtension
        } = useAutoAttendantsListLogic(dispatch);


    const data = useSelector(
        (state: ReduxState) => state.autoAttendants.autoAttendantList,
    );

    const {deletingInProgress} = useSelector(
        (state: ReduxState) => state.autoAttendants,
    );

    useEffect(() => {
        dispatch(actions.getCallQueues.request());
    }, []);

    const fetchData = (params?:{pageSize:number, page:number}, filters?:AAListFilters) => {
        dispatch(actions.getAutoAttendantsList.request({
            limit: params?.pageSize || 10,
            offset: params?.page && params?.pageSize ? params.page * params.pageSize : 0,
            filters
        }));
    }

    const fetchMenu = (id_pin: string, i_account: number) => {
        dispatch(actions.getAutoAttendantMenuList.request({ id_pin, i_account }));
    };

    const handleForceFirstPage = () => {
        setForceFirstPage(true);
    };

    const handleStatusChange = () =>
        setCreateNewAutoAttendantModalVisible(true);


    const toggleCreateNewVisibility = () =>
        setCreateNewAutoAttendantModalVisible(
            !createNewAutoAttendantModalVisible,
        );

    const columns = useMemo(() => {
        return isSupervisor
            ? generateSvColumns(t, fetchMenu, classes, showCallHistory)
            : generateColumns(t, fetchMenu, classes, showCallHistory, showRemoveExtensionModal);
    }, [isSupervisor, t]);

    const handlePageChange = (param: PageChangeEvent) => {
        setPage(param.page);
    };

    const handlePageSizeChange = (param: PageChangeEvent) => {
        setPageSize(param.pageSize);
        setPage(0);
    };

    return (
        <PermissionPlaceholder permission={Permission.CloudPBX.AutoAttendants.value}>
            <div className={classes.mainContainer}>
                <div className={classes.scrollable}>
                    <PermissionProvider
                        permission={
                            Permission.CloudPBX.AutoAttendants.AddNewAutoAttendant
                                .value
                        }
                    >
                        <Header
                            title={t('screens:autoAttendants.listTitle')}
                            totalNumber={data?.total || 0}
                            ofNumber={data?.disabled || 0}
                            dataQa={'device-list-view-header'}
                            buttonText={t('common:addNewButton')}
                            ofText={t('screens:autoAttendants.disabled')}
                            buttonOnClick={handleStatusChange}
                            plusIcon={true}
                            customClass={classes.header}
                        />
                    </PermissionProvider>

                    <FiltersComponent
                        page={page}
                        pageSize={pageSize}
                        handleSearch={fetchData}
                        handleForceFirstPage={handleForceFirstPage}
                        triggerSearch={forceSearch}
                    />

                    <DataGrid<AutoAttendantListItem>
                        className={classes.table}
                        classes={{tableContainer: classes.tableContainer}}
                        columns={columns}
                        data={data?.items || []}
                        rowCount={data?.total || 0}
                        loading={data?.isLoading || deletingInProgress || false}
                        paginationMode={PaginationMode.Server}
                        centeredRows
                        narrowRows
                        initialPageSize={10}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        forceFirstPage={forceFirstPage}
                    />
                </div>
                <CallHistoryDialog
                    isOpen={!!callHistoryModalConfig}
                    toggleVisibility={resetCallHistoryDialog}
                    accountId={callHistoryModalConfig?.accountId}
                    customHeader={t('screens:autoAttendants.callActivity', {
                        name: callHistoryModalConfig?.name || '',
                    })}
                    downloadRecordingPermission={
                        Permission.CloudPBX.AutoAttendants.ViewCallHistory.DownloadCallRecording.value
                    }
                />

                <Dialog
                    isOpen={createNewAutoAttendantModalVisible}
                    toggleVisibility={toggleCreateNewVisibility}
                />

                <AlertDialog
                    isOpen={!!removeModal?.isOpen}
                    dataQa={'delete-auto-attendant-modal'}
                    contentClass="medium-width-modal-delete"
                    hideHeader={true}
                    description={t('screens:autoAttendants.deleteMsg', {
                        value: removeModal?.name,
                    })}
                    className={classes.deleteDialogButtons}
                    dialogActionsButtons={[
                        <DialogButton
                            key="cancel"
                            label={t('common:cancel')}
                            onClick={closeRemoveDialog}
                        />,
                        <DialogButton
                            key="delete"
                            label={t('common:delete')}
                            onClick={deleteExtension}
                            dataTestId={'auto-attendant-delete-button'}
                        />,
                    ]}
                />
            </div>
        </PermissionPlaceholder>

    );
};

export default AutoAttendantsList;
